import React, { useEffect, useState, useRef } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import api from '../utils/api';

const AVATAR_CLASS = {
  'orange-500': 'bg-orange-500',
  'amber-500': 'bg-amber-500',
  'rose-500': 'bg-rose-500',
  'emerald-500': 'bg-emerald-500',
  'sky-500': 'bg-sky-500',
  'violet-500': 'bg-violet-500',
  'fuchsia-500': 'bg-fuchsia-500',
  'yellow-500': 'bg-yellow-500',
};

const RatingStars = ({ value = 5 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={16}
        className={i <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
      />
    ))}
  </div>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average: 0, count: 0 });
  const scrollerRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get('/reviews?limit=12'), api.get('/reviews/stats')])
      .then(([r, s]) => {
        setReviews(r.data);
        setStats(s.data);
      })
      .catch(() => {});
  }, []);

  if (reviews.length === 0) return null;

  const scroll = (dir) => {
    if (!scrollerRef.current) return;
    const card = scrollerRef.current.querySelector('[data-testid^="review-card-"]');
    const step = card ? card.getBoundingClientRect().width + 24 : 320;
    scrollerRef.current.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <section data-testid="reviews-section" className="py-16 bg-orange-50/30">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="uppercase tracking-widest text-xs text-orange-600 font-semibold mb-2">
              Customer Reviews
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-orange-900">
              What Our Devotees Say
            </h2>
            <div className="flex items-center gap-3 mt-3">
              <RatingStars value={Math.round(stats.average)} />
              <span data-testid="reviews-average" className="font-semibold text-gray-800">
                {stats.average.toFixed(1)} / 5
              </span>
              <span className="text-gray-500 text-sm">
                · based on {stats.count} verified review{stats.count === 1 ? '' : 's'}
              </span>
            </div>
          </div>
          <div className="hidden md:flex gap-2">
            <button
              data-testid="reviews-scroll-prev"
              onClick={() => scroll(-1)}
              className="w-10 h-10 rounded-full border border-orange-300 text-orange-700 hover:bg-orange-100 flex items-center justify-center"
              aria-label="Previous review"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              data-testid="reviews-scroll-next"
              onClick={() => scroll(1)}
              className="w-10 h-10 rounded-full border border-orange-300 text-orange-700 hover:bg-orange-100 flex items-center justify-center"
              aria-label="Next review"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-thin"
          style={{ scrollbarWidth: 'thin' }}
        >
          {reviews.map((r) => (
            <article
              key={r.id}
              data-testid={`review-card-${r.id}`}
              className="snap-start shrink-0 w-[88%] sm:w-[60%] md:w-[32%] bg-white rounded-2xl border border-orange-100 p-6 shadow-sm hover:shadow-md transition"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-11 h-11 rounded-full ${AVATAR_CLASS[r.avatar_color] || 'bg-orange-500'} text-white font-bold flex items-center justify-center`}
                    aria-hidden="true"
                  >
                    {r.author_initial || (r.author_name?.[0] || '?').toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 leading-tight">{r.author_name}</p>
                    {r.location && (
                      <p className="text-xs text-gray-500">{r.location}</p>
                    )}
                  </div>
                </div>
                {r.source === 'google' && (
                  <span className="text-[10px] uppercase tracking-wider text-blue-600 font-semibold">
                    Google
                  </span>
                )}
              </div>
              <RatingStars value={r.rating} />
              <Quote className="text-orange-200 mt-3" size={28} />
              <p className="text-gray-700 leading-relaxed mt-1 line-clamp-6">{r.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
